import { UpdateAdminUserRoleRequest, UpdateMyProfileRequest } from "@givenwell/management-api";
import { queryOptions, useMutation, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { api, auth } from "./utils";

export function useUpdateMyProfileMutation() {
  return useMutation({
    mutationFn: async (requestBody: Omit<UpdateMyProfileRequest, "refresh_token">) => {
      const refreshToken = auth.getRefreshToken();
      const result = await api.user.updateMyUserProfile({
        requestBody: { ...requestBody, refresh_token: refreshToken ?? undefined },
      });
      if (result.access_token) {
        auth.saveAuthTokens({ access_token: result.access_token, refresh_token: result.refresh_token ?? undefined });
      }
    },
  });
}

export const permissionsQuery = queryOptions({
  queryKey: ["permissions"],
  queryFn: async () => {
    return await api.user.getMyPermissions();
  },
});

// TODO: end-to-end typesafety
export const Permission = {
  Supplier: {
    Default: "sppl.default",
    Admin: "sppl.*",
  },
  Supporter: {
    Default: "supp.default",
    Admin: "supp.*",
  },
  Admin: {
    Default: "admin.default",
    ManageClaims: "admin.manage_claims",
    ViewMemberData: "admin.view_member_data",
    ManageRoles: "admin.manage_roles",
    ManageMemberAccounts: "admin.manage_member_accounts",
    ExtendExpirations: "admin.extend_expirations",
    Dev: "admin.*",
  },
} as const;

/**
 * Admin permission groups that can be assigned from the staff page. `group.admin_dev` carries `admin.*`
 * and is no longer assignable via the API.
 */
export const AdminGroup = {
  Default: "group.admin_default",
  Support: "group.admin_support",
} as const;

export function usePermissions(scope?: string) {
  const q = useQuery(permissionsQuery);
  const applicablePermissions = useMemo(
    () => [...(q.data?.admin || []), ...(scope ? (q.data?.[scope] ?? []) : [])],
    [q.data, scope],
  );
  return (...perms: string[]) => {
    if (applicablePermissions.some(p => p.endsWith(".*"))) {
      return true;
    }
    return perms.some(perm => applicablePermissions.includes(perm));
  };
}

export const adminUsersQuery = queryOptions({
  queryKey: ["admin-users"],
  queryFn: async () => {
    return await api.user.getAdminUsers();
  },
});

export function useUpdateRoleMutation() {
  return useMutation({
    mutationFn: async (requestBody: UpdateAdminUserRoleRequest) => {
      api.user.updateAdminUserRole({ requestBody });
    },
  });
}
